import app, { db } from '../auth/firebaseConfig';
import {
  doc, getDoc, setDoc, updateDoc, deleteDoc,
  collection, getDocs, query, where, orderBy
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

const ROLES_COLLECTION = 'ubunited_roles';
const CONTENT_COLLECTION = 'ubunited_content';
const CONFIG_COLLECTION = 'ubunited_config';
const SUGGESTIONS_COLLECTION = 'ubunited_suggestions';
const MODERATION_STATE_COLLECTION = 'ubunited_moderation_state';

// --- Role Management ---

export const UBU_ROLES = [
  { id: 'super_admin', label: 'Super Admin' },
  { id: 'portal_admin', label: 'SportPortal Admin' },
  { id: 'ambassador', label: 'SportPortal Ambassador' },
  { id: 'sport_ambassador', label: 'Sport Ambassador' },
  { id: 'content_moderator', label: 'Content Moderator' },
  { id: 'member_moderator', label: 'Member Moderator' },
];

export function roleLabel(roleId) {
  return UBU_ROLES.find(r => r.id === roleId)?.label || roleId;
}

export async function getUserRole(userId, email) {
  // Check by userId first
  const docRef = doc(db, ROLES_COLLECTION, userId);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return docSnap.data();
  }
  // Fall back to email lookup (for roles granted before user logged in)
  if (email) {
    const byEmail = await getUserRoleByEmail(email);
    if (byEmail) {
      // Re-key the doc under the real UID server-side (clients can't write roles)
      try {
        const functions = getFunctions(app, 'us-east1');
        const migrate = httpsCallable(functions, 'ubuMigrateRole');
        await migrate();
      } catch (e) {
        console.warn('Role migration call failed (role still usable):', e);
      }
      return byEmail;
    }
  }
  return null;
}

export async function getUserRoleByEmail(email) {
  const q = query(collection(db, ROLES_COLLECTION), where('email', '==', email));
  const snapshot = await getDocs(q);
  if (!snapshot.empty) {
    const docData = snapshot.docs[0];
    return { id: docData.id, ...docData.data() };
  }
  return null;
}

export async function isAdmin(userId, email) {
  const role = await getUserRole(userId, email);
  return role?.role === 'super_admin';
}

export async function isAmbassador(userId, email) {
  const role = await getUserRole(userId, email);
  return role?.role === 'ambassador' || role?.role === 'super_admin';
}

export async function setUserRole(userId, email, role, grantedBy) {
  await setDoc(doc(db, ROLES_COLLECTION, userId), {
    email,
    role,
    grantedBy,
    grantedAt: new Date().toISOString(),
    active: true,
  });
}

export async function removeUserRole(userId) {
  await deleteDoc(doc(db, ROLES_COLLECTION, userId));
}

export async function getAllRoles() {
  const snapshot = await getDocs(collection(db, ROLES_COLLECTION));
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
}

// --- Ambassador Content ---

const contentCache = {};
const CONTENT_CACHE_TTL = 3 * 60 * 1000; // 3 minutes

function invalidateContentCache() {
  Object.keys(contentCache).forEach(k => delete contentCache[k]);
}

export async function submitContent(userId, userEmail, userName, contentData) {
  const docRef = doc(collection(db, CONTENT_COLLECTION));
  await setDoc(docRef, {
    ...contentData,
    authorId: userId,
    authorEmail: userEmail,
    authorName: userName,
    status: contentData.status || 'pending',
    featured: contentData.featured || false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });
  invalidateContentCache();
  return docRef.id;
}

export async function getContent(filters = {}) {
  const cacheKey = `content_${filters.status || 'all'}_${filters.type || 'all'}`;
  const cached = contentCache[cacheKey];
  if (cached && Date.now() - cached.time < CONTENT_CACHE_TTL) return cached.data;

  const constraints = [];
  if (filters.status) constraints.push(where('status', '==', filters.status));
  if (filters.type) constraints.push(where('type', '==', filters.type));
  constraints.push(orderBy('createdAt', 'desc'));
  const q = query(collection(db, CONTENT_COLLECTION), ...constraints);
  const snapshot = await getDocs(q);
  const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  contentCache[cacheKey] = { data, time: Date.now() };
  return data;
}

export async function getContentItem(contentId) {
  const snap = await getDoc(doc(db, CONTENT_COLLECTION, contentId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function getContentByAuthor(userId) {
  const q = query(
    collection(db, CONTENT_COLLECTION),
    where('authorId', '==', userId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getFeaturedContent() {
  const cacheKey = 'content_featured';
  const cached = contentCache[cacheKey];
  if (cached && Date.now() - cached.time < CONTENT_CACHE_TTL) return cached.data;

  const q = query(
    collection(db, CONTENT_COLLECTION),
    where('status', '==', 'approved'),
    where('featured', '==', true),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  contentCache[cacheKey] = { data, time: Date.now() };
  return data;
}

export async function approveContent(contentId) {
  await updateDoc(doc(db, CONTENT_COLLECTION, contentId), {
    status: 'approved',
    updatedAt: new Date().toISOString(),
  });
  invalidateContentCache();
}

export async function rejectContent(contentId) {
  await updateDoc(doc(db, CONTENT_COLLECTION, contentId), {
    status: 'rejected',
    updatedAt: new Date().toISOString(),
  });
  invalidateContentCache();
}

export async function toggleFeatured(contentId, featured) {
  await updateDoc(doc(db, CONTENT_COLLECTION, contentId), {
    featured,
    updatedAt: new Date().toISOString(),
  });
  invalidateContentCache();
}

export async function updateContent(contentId, updates) {
  await updateDoc(doc(db, CONTENT_COLLECTION, contentId), {
    ...updates,
    updatedAt: new Date().toISOString(),
  });
  invalidateContentCache();
}

export async function deleteContent(contentId) {
  await deleteDoc(doc(db, CONTENT_COLLECTION, contentId));
  invalidateContentCache();
}

// --- Site Config (Pickleball Layout, Resources, etc.) ---

const configCache = {};
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export async function getSiteConfig(configKey) {
  const cached = configCache[configKey];
  if (cached && Date.now() - cached.time < CACHE_TTL) {
    return cached.data;
  }
  try {
    const docSnap = await getDoc(doc(db, CONFIG_COLLECTION, configKey));
    const data = docSnap.exists() ? docSnap.data() : null;
    configCache[configKey] = { data, time: Date.now() };
    return data;
  } catch (e) {
    console.error(`Error loading config ${configKey}:`, e);
  }
  return null;
}

export async function setSiteConfig(configKey, data) {
  await setDoc(doc(db, CONFIG_COLLECTION, configKey), {
    ...data,
    updatedAt: new Date().toISOString(),
  });
  // Invalidate cache so the next read gets fresh data
  delete configCache[configKey];
}

// --- Content Suggestions ---

export async function submitSuggestion(userId, userName, data) {
  const docRef = doc(collection(db, SUGGESTIONS_COLLECTION));
  await setDoc(docRef, {
    ...data,
    suggestedBy: userId,
    suggestedByName: userName,
    status: 'pending',
    createdAt: new Date().toISOString(),
  });
  return docRef.id;
}

export async function getSuggestions(status) {
  const constraints = [];
  if (status) constraints.push(where('status', '==', status));
  constraints.push(orderBy('createdAt', 'desc'));
  const q = query(collection(db, SUGGESTIONS_COLLECTION), ...constraints);
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getSuggestionsByUser(userId) {
  const q = query(
    collection(db, SUGGESTIONS_COLLECTION),
    where('suggestedBy', '==', userId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function updateSuggestionStatus(suggestionId, status, reviewedBy) {
  await updateDoc(doc(db, SUGGESTIONS_COLLECTION, suggestionId), {
    status,
    reviewedBy,
    reviewedAt: new Date().toISOString(),
  });
}

// --- Moderation State (per-moderator last review timestamp) ---

export async function getModerationState(userId) {
  try {
    const snap = await getDoc(doc(db, MODERATION_STATE_COLLECTION, userId));
    return snap.exists() ? snap.data() : null;
  } catch (e) {
    return null;
  }
}

export async function markAllReviewed(userId) {
  await setDoc(doc(db, MODERATION_STATE_COLLECTION, userId), {
    lastReviewedAt: new Date().toISOString(),
  });
}

// --- Members (for member_moderator dashboard) ---

export async function getAllUsers() {
  // The users collection is SHARED with the ECAC app (same Firebase project)
  // and contains its accounts plus old test seeds — only count ours.
  const q = query(collection(db, 'users'), where('signupSite', '==', 'ubunited'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => {
    const data = d.data();
    // Normalize createdAt: our docs store ISO strings, but legacy/test docs
    // used Firestore Timestamps — a Timestamp in a string sort throws
    const createdAt = typeof data.createdAt === 'string'
      ? data.createdAt
      : data.createdAt?.toDate?.().toISOString() || '';
    return { id: d.id, ...data, createdAt };
  });
}
